import { useState } from "react";
import {
  Button,
  Popover,
  Popper,
  Box,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  IconButton,
  Badge,
  Fade,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

export const FiltersButton = ({ filters, setFilters, filtersDisabled }) => {
  // Popover (the filter UI)
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);
  const openPopover = Boolean(popoverAnchorEl);
  const popoverId = openPopover ? "filters-popover" : undefined;
  const handleClick = (event) => setPopoverAnchorEl(event.currentTarget);
  const handleClose = () => setPopoverAnchorEl(null);

  // Popper (when button is disabled)
  const [disabledAnchorEl, setDisabledAnchorEl] = useState(null);
  const openDisabledPopper = Boolean(disabledAnchorEl);

  const handleDisabledEnter = (event) => {
    if (filtersDisabled) setDisabledAnchorEl(event.currentTarget);
  };
  const handleDisabledLeave = () => setDisabledAnchorEl(null);

  const handleToggle = (key) => (event) =>
    setFilters((prev) => ({ ...prev, [key]: event.target.checked }));

  const clearAll = () =>
    setFilters({ notReady: false, today: false, yesterday: false, last7: false });

  const activeCount = Object.values(filters || {}).filter(Boolean).length;

  const startIcon =
    activeCount > 0 ? (
      <Badge
        badgeContent={activeCount}
        color="primary"
        overlap="circular"
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ paddingRight: "15px", marginRight: "5px" }}
      >
      </Badge>
    ) : (
      <FilterListIcon />
    );

  return (
    <>
      <span
        onMouseEnter={handleDisabledEnter}
        onMouseLeave={handleDisabledLeave}
        onFocus={handleDisabledEnter}
        onBlur={handleDisabledLeave}
        style={{ display: "inline-block" }}
      >
        <Button
          data-testid="filters-button"
          variant="text"
          startIcon={startIcon}
          onClick={handleClick}
          aria-describedby={popoverId}
          sx={{ backgroundColor: activeCount > 0 && "#ADCBE8" }}
          disabled={filtersDisabled}
        >
          Filters
        </Button>
      </span>

      {/* Popper shown when button is disabled */}
      <Popper
        open={openDisabledPopper}
        anchorEl={disabledAnchorEl}
        placement="bottom"
        transition
        modifiers={[
          {
            name: "offset",
            options: { offset: [0, 10] },
          },
        ]}
        sx={{ zIndex: 999 }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={120}>
            <Box
              sx={{
                bgcolor: "#EAF6FB", // soft blue background
                borderRadius: 1,
                px: 2,
                py: 1,
                boxShadow: 1,
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                maxWidth: 360,
              }}
              role="tooltip"
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <InfoOutlinedIcon sx={{ color: "#0A5F92", fontSize: 18 }} />
              </Box>

              <Typography
                variant="body2"
                sx={{ color: "#0A5F92", fontWeight: 500, whiteSpace: "nowrap" }}
              >
                Clear search to use filters
              </Typography>
            </Box>
          </Fade>
        )}
      </Popper>

      <Popover
        id={popoverId}
        open={openPopover}
        anchorEl={popoverAnchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            p: 2,
            minWidth: 280,
            borderRadius: 1.25,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Typography variant="h6">Choose filters</Typography>
          <IconButton size="small" onClick={handleClose} aria-label="close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 1 }} />

        {/* File status */}
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          File status
        </Typography>
        <Box sx={{ borderRadius: 1, p: 1 }}>
          <FormGroup>
            <FormControlLabel
              sx={{ backgroundColor: filters.notReady && "#eef7fb" }}
              control={
                <Checkbox checked={filters.notReady} onChange={handleToggle("notReady")} />
              }
              label="Not ready"
            />
          </FormGroup>
        </Box>

        {/* Plan modified */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Plan modified
          </Typography>

          <Box sx={{ borderRadius: 1, p: 1 }}>
            <FormGroup>
              <FormControlLabel
                sx={{ backgroundColor: filters.today && "#eef7fb" }}
                control={<Checkbox checked={filters.today} onChange={handleToggle("today")} />}
                label="Today"
              />
              <FormControlLabel
                sx={{ backgroundColor: filters.yesterday && "#eef7fb" }}
                control={<Checkbox checked={filters.yesterday} onChange={handleToggle("yesterday")} />}
                label="Yesterday"
              />
              <FormControlLabel
                sx={{ backgroundColor: filters.last7 && "#eef7fb" }}
                control={<Checkbox checked={filters.last7} onChange={handleToggle("last7")} />}
                label="Last 7 days"
              />
            </FormGroup>
          </Box>
        </Box>

        <Box
          sx={{
            mt: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Button component="button" onClick={clearAll}>
            CLEAR ALL FILTERS
          </Button>
        </Box>
      </Popover>
    </>
  );
};
